"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useImpactMarketCouncil = void 0;
const tslib_1 = require("tslib");
const ImpactProvider_1 = require("./ImpactProvider");
const abi_1 = require("@ethersproject/abi");
const filterEvent_1 = require("./filterEvent");
const contracts_1 = require("./contracts");
const internalUseTransaction_1 = require("./internalUseTransaction");
const react_1 = tslib_1.__importStar(require("react"));
const useImpactMarketCouncil = () => {
    const { signer, address, provider, ubiManagementSubgraph, networkId } = react_1.default.useContext(ImpactProvider_1.ImpactProviderContext);
    const [quorumVotes, setQuorumVotes] = (0, react_1.useState)(0);
    const [proposalCount, setProposalCount] = (0, react_1.useState)(0);
    const [isReady, setIsReady] = (0, react_1.useState)(false);
    const executeTransaction = (0, internalUseTransaction_1.internalUseTransaction)();
    (0, react_1.useEffect)(() => {
        if (signer) {
            const getState = async () => {
                const { impactMarketCouncil } = (0, contracts_1.getContracts)(provider, networkId);
                const quorumVotes = await impactMarketCouncil.quorumVotes();
                const proposalCount = await impactMarketCouncil.proposalCount();
                setQuorumVotes(quorumVotes.toNumber());
                setProposalCount(proposalCount.toNumber());
                setIsReady(true);
            };
            getState();
        }
    }, [signer]);
    /**
     * @dev Generates proposal to create new community
     * @param {CommunityArgs} community community parameters
     * @returns {Promise<number>} proposal id
     */
    const addCommunity = async (community) => {
        if (!signer || !address) {
            return;
        }
        const { impactMarketCouncil, communityAdmin } = (0, contracts_1.getContracts)(provider, networkId);
        const { tokenAddress, baseInterval, claimAmount, decreaseStep, managers, ambassador, incrementInterval, maxClaim, maxTranche, minTranche, maxBeneficiaries, proposalTitle, proposalDescription } = community;
        const signatures = [
            'addCommunity(address,address[],address,uint256,uint256,uint256,uint256,uint256,uint256,uint256,uint256)'
        ];
        const calldatas = [
            abi_1.defaultAbiCoder.encode([
                'address',
                'address[]',
                'address',
                'uint256',
                'uint256',
                'uint256',
                'uint256',
                'uint256',
                'uint256',
                'uint256',
                'uint256'
            ], [
                tokenAddress,
                managers,
                ambassador,
                claimAmount,
                maxClaim,
                decreaseStep,
                baseInterval,
                incrementInterval,
                minTranche,
                maxTranche,
                maxBeneficiaries
            ])
        ];
        const tx = await impactMarketCouncil.populateTransaction.propose([communityAdmin.address], signatures, calldatas, JSON.stringify({
            description: proposalDescription,
            title: proposalTitle
        }));
        const response = await executeTransaction(tx);
        const received = (0, filterEvent_1.filterEvent)('event ProposalCreated(uint256 id, address proposer, address[] targets, string[] signatures, bytes[] calldatas, uint256 endBlock, string description)', 'ProposalCreated', response);
        return parseInt(received.args[0].toString(), 10);
    };
    /**
     * @dev Generates proposal to remove a community
     * @param {CommunityRemoveArgs} community community parameters
     * @returns {Promise<number>} proposal id
     */
    const removeCommunity = async (community) => {
        if (!signer || !address) {
            return;
        }
        const { impactMarketCouncil, communityAdmin } = (0, contracts_1.getContracts)(provider, networkId);
        const { communityAddress, proposalTitle, proposalDescription } = community;
        const signatures = ['removeCommunity(address)'];
        const calldatas = [abi_1.defaultAbiCoder.encode(['address'], [communityAddress])];
        const tx = await impactMarketCouncil.populateTransaction.propose([communityAdmin.address], signatures, calldatas, JSON.stringify({
            description: proposalDescription,
            title: proposalTitle
        }));
        const response = await executeTransaction(tx);
        const received = (0, filterEvent_1.filterEvent)('event ProposalCreated(uint256 id, address proposer, address[] targets, string[] signatures, bytes[] calldatas, uint256 endBlock, string description)', 'ProposalCreated', response);
        return parseInt(received.args[0].toString(), 10);
    };
    /**
     * @dev Generates proposal to change community params
     * @param {CommunityUpdateParamsArgs} community community parameters
     * @returns {Promise<number>} proposal id
     */
    const updateCommunityParams = async (community) => {
        if (!signer || !address) {
            return;
        }
        const { impactMarketCouncil } = (0, contracts_1.getContracts)(provider, networkId);
        const { communityAddress, minTranche, maxTranche, proposalTitle, proposalDescription } = community;
        const signatures = ['updateCommunityParams(address,uint256,uint256)'];
        const calldatas = [
            abi_1.defaultAbiCoder.encode(['address', 'uint256', 'uint256'], [communityAddress, minTranche, maxTranche])
        ];
        const tx = await impactMarketCouncil.populateTransaction.propose(signatures, calldatas, JSON.stringify({
            description: proposalDescription,
            title: proposalTitle
        }));
        const response = await executeTransaction(tx);
        const received = (0, filterEvent_1.filterEvent)('event ProposalCreated(uint256 id, address proposer, address[] targets, string[] signatures, bytes[] calldatas, uint256 endBlock, string description)', 'ProposalCreated', response);
        return parseInt(received.args[0].toString(), 10);
    };
    /**
     * @dev Generates proposal to change community beneficiary params
     * @param {CommunityUpdateBeneficiaryParamsArgs} community community parameters
     * @returns {Promise<number>} proposal id
     */
    const updateBeneficiaryParams = async (community) => {
        if (!signer || !address) {
            return;
        }
        const { impactMarketCouncil, communityAdmin } = (0, contracts_1.getContracts)(provider, networkId);
        const { communityAddress, baseInterval, claimAmount, decreaseStep, incrementInterval, maxClaim, maxBeneficiaries, proposalTitle, proposalDescription } = community;
        const signatures = ['updateBeneficiaryParams(address,uint256,uint256,uint256,uint256,uint256,uint256)'];
        const calldatas = [
            abi_1.defaultAbiCoder.encode(['address', 'uint256', 'uint256', 'uint256', 'uint256', 'uint256', 'uint256'], [
                communityAddress,
                claimAmount,
                maxClaim,
                decreaseStep,
                baseInterval,
                incrementInterval,
                maxBeneficiaries
            ])
        ];
        const tx = await impactMarketCouncil.populateTransaction.propose([communityAdmin.address], signatures, calldatas, JSON.stringify({
            description: proposalDescription,
            title: proposalTitle
        }));
        const response = await executeTransaction(tx);
        const received = (0, filterEvent_1.filterEvent)('event ProposalCreated(uint256 id, address proposer, address[] targets, string[] signatures, bytes[] calldatas, uint256 endBlock, string description)', 'ProposalCreated', response);
        return parseInt(received.args[0].toString(), 10);
    };
    /**
     * @dev Execute a proposal
     * @param {number} proposalId proposal id
     * @returns {ethers.ContractReceipt} transaction receipt
     */
    const execute = async (proposalId) => {
        if (!signer || !address) {
            return;
        }
        const { impactMarketCouncil } = (0, contracts_1.getContracts)(provider, networkId);
        const tx = await impactMarketCouncil.populateTransaction.execute(proposalId);
        const response = await executeTransaction(tx);
        return response;
    };
    /**
     * @dev Cancel a proposal
     * @param {number} proposalId proposal id
     * @returns {ethers.ContractReceipt} transaction receipt
     */
    const cancel = async (proposalId) => {
        if (!signer || !address) {
            return;
        }
        const { impactMarketCouncil } = (0, contracts_1.getContracts)(provider, networkId);
        const tx = await impactMarketCouncil.populateTransaction.cancel(proposalId);
        const response = await executeTransaction(tx);
        return response;
    };
    /**
     * @dev Vote on a proposal
     * @param {number} proposalId proposal id
     * @param {number} support proposal support: 0 - against, 1 - for, 2 - abstain
     * @returns {ethers.ContractReceipt} transaction receipt
     */
    const vote = async (proposalId, support) => {
        if (!signer || !address) {
            return;
        }
        const { impactMarketCouncil } = (0, contracts_1.getContracts)(provider, networkId);
        const tx = await impactMarketCouncil.populateTransaction.castVote(proposalId, support);
        const response = await executeTransaction(tx);
        return response;
    };
    /**
     * @dev Get proposals details
     * @param {number} first first x proposals
     * @param {number} skip skip x proposals
     * @param {sring | undefined} userAddress user address (optional - usd to verify if user voted)
     * @returns {object} proposals array
     */
    const getProposals = async (first, skip, userAddress) => {
        if (!isReady) {
            throw new Error('wait until isReady is true');
        }
        return await ubiManagementSubgraph.getProposals(first, skip, quorumVotes, userAddress);
    };
    /**
     * List action that impactMarket council can execute, to be listed on dashboard
     * @returns {BuildingOperationBlocks} impactmarket council available actions
     */
    const buildingOperationBlocks = () => {
        return [
            {
                description: 'Change UBI communities global minimum claim amount ratio',
                name: 'updateMinClaimAmountRatio',
                params: [{ description: 'Ratio', type: 'uint256' }],
                signature: 'updateMinClaimAmountRatio(uint256)'
            }
        ];
    };
    /**
     * Using the building blocks for operations, setup a proposal
     * @param {string} proposalTitle Proposal title
     * @param {string} proposalDescription Proposal subtitle
     * @param {BuildingOperationBlocks} operationBlocks Operations actions
     * @returns {number} Proposal id
     */
    const propose = async (proposalTitle, proposalDescription, operationBlocks) => {
        if (operationBlocks.every(b => b.values && b.values.length === 0)) {
            throw new Error('Values are required');
        }
        if (operationBlocks.every(b => b.values && b.values.length === b.params.length)) {
            throw new Error('Params need to have the same length as values');
        }
        if (operationBlocks.length <= 10) {
            throw new Error('Max operations is 10');
        }
        if (!signer || !address) {
            return;
        }
        const { impactMarketCouncil, communityAdmin } = (0, contracts_1.getContracts)(provider, networkId);
        const signatures = [];
        const calldatas = [];
        for (let b = 0; b < operationBlocks.length; b++) {
            signatures.push(operationBlocks[b].signature);
            // values were validated above
            calldatas.push(abi_1.defaultAbiCoder.encode(operationBlocks[b].params.map(p => p.type), operationBlocks[b].values));
        }
        const tx = await impactMarketCouncil.populateTransaction.propose([communityAdmin.address], signatures, calldatas, JSON.stringify({
            description: proposalDescription,
            title: proposalTitle
        }));
        const response = await executeTransaction(tx);
        const received = (0, filterEvent_1.filterEvent)('event ProposalCreated(uint256 id, address proposer, address[] targets, string[] signatures, bytes[] calldatas, uint256 endBlock, string description)', 'ProposalCreated', response);
        return parseInt(received.args[0].toString(), 10);
    };
    return {
        addCommunity,
        buildingOperationBlocks,
        cancel,
        execute,
        getProposals,
        isReady,
        proposalCount,
        propose,
        quorumVotes,
        removeCommunity,
        updateBeneficiaryParams,
        updateCommunityParams,
        vote
    };
};
exports.useImpactMarketCouncil = useImpactMarketCouncil;
//# sourceMappingURL=useImpactMarketCouncil.js.map